import { SaleInvoiceDetailFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { formatTaxRate } from 'modules/finance/lib/invoices'

import { ExtraRow } from './_ExtraRow'

interface LineItemsExtraRowsProps {
  currency: string
  locale: string
  lineItems: NonNullable<SaleInvoiceDetailFragment['lineItems']>
  totalAmountCents?: number
  discountPercentage?: number
  taxes?: NonNullable<SaleInvoiceDetailFragment['taxes']>
  extraEmptyColumns?: number
}

const LineItemsExtraRows = ({
  currency,
  locale,
  lineItems,
  totalAmountCents = 0,
  discountPercentage = 0,
  taxes = [],
  extraEmptyColumns = 0,
}: LineItemsExtraRowsProps) => {
  const subtotal =
    lineItems.reduce((acc, lineItem) => acc + lineItem.amountCents * lineItem.quantity, 0) || 0
  const total = totalAmountCents

  const discountAmountCents = subtotal * discountPercentage

  const totalExcTaxes = subtotal - discountAmountCents

  return (
    <>
      <ExtraRow
        columnFour={i18n.t('finance.sale_invoices.detail.line_items.subtotal')}
        columnFive={formattedAmount({
          amountCents: subtotal,
          currency,
          locale,
        })}
        extraEmptyColumns={extraEmptyColumns}
      />
      {discountAmountCents > 0 && (
        <ExtraRow
          columnFour={i18n.t('finance.sale_invoices.detail.line_items.discount', {
            discount_percentage: discountPercentage * 100,
          })}
          columnFive={`-${formattedAmount({
            amountCents: discountAmountCents,
            currency,
            locale,
          })}`}
          extraEmptyColumns={extraEmptyColumns}
        />
      )}
      <ExtraRow
        columnFour={i18n.t('finance.sale_invoices.detail.line_items.total_exc_tax')}
        columnFive={formattedAmount({
          amountCents: totalExcTaxes,
          currency,
          locale,
        })}
        extraEmptyColumns={extraEmptyColumns}
      />
      {taxes.map((tax, index) => (
        <ExtraRow
          key={`tax_${index}`}
          columnFour={i18n.t('finance.sale_invoices.detail.line_items.tax_total_v2', {
            tax_name: tax.taxTypeName || '',
            tax_percentage: formatTaxRate(tax.percentage),
            tax_base_amount: formattedAmount({
              amountCents: tax.baseAmountCents || 0,
              currency,
              locale,
            }),
          })}
          columnFive={(() => {
            const amount = formattedAmount({
              amountCents: tax.amountCents || 0,
              currency,
              locale,
            })
            return tax.type === 'vat' ? amount : `-${amount}`
          })()}
          extraEmptyColumns={extraEmptyColumns}
        />
      ))}
      <ExtraRow
        columnFour={i18n.t('finance.sale_invoices.detail.line_items.total')}
        columnFive={formattedAmount({
          amountCents: total,
          currency,
          locale,
        })}
        extraEmptyColumns={extraEmptyColumns}
      />
    </>
  )
}

export default LineItemsExtraRows
